import Features from "components/common/Features";
import InfoSlider from "components/common/InfoSlider";
import IndustryLeadersCards from "components/common/sliders/IndustryLeadersCards";
import { graphql, StaticQuery } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";

import AboutSection from "./updated/AboutSection";
import WebinarHeader from "./updated/WebinarHeader";

export default function WebinarPageV2({ title }) {
  const renderContent = (data) => {
    const pageData = {
      header: {
        preTitle: "On-demand Webinar",
        title,
        formId: "d8c55e02-c0a8-42a9-b29d-9d323533c35e",
        formTitle: "Watch the Webinar",
        image: data.headerImage,
      },

      aboutSection: {
        title: "About the Webinar",
        description:
          "Traditional security systems host widespread issues, including unreliable hardware, difficult-to-use software, and cybersecurity vulnerabilities. Learn how some of the biggest companies in the world use cloud-managed solutions to eliminate inefficiencies and improve operations with simple, secure, and scalable system architecture.",
        learnTitle: "What You'll Learn in 45 Minutes",
        learnLines: [
          "Why a cloud-native solution is superior to on-premise video security systems",
          "The benefits of using a unified platform with smart cameras, sensors, and integrations",
          "How to improve safety and productivity with powerful AI analytics",
        ],
        speakers: [
          {
            name: "Jermey Gulley",
            title: "Director of Technology Partnerships",
            company: "Rhombus",
            image: data.jeremy,
          },
          {
            name: "Trevor Bernhard",
            title: "Sales Engineer",
            company: "Rhombus",
            image: data.trevor,
          },
        ],
      },
      benefits: {
        list: [
          {
            title: "Enjoy an all-in-one solution that grows with your needs.",
            tabTitle: "Infinite Scalability",

            description:
              "With a broad suite of products that is constantly growing, Rhombus provides a true single-pane-of-glass experience. Remotely manage access control, security cameras, alarms, sensors, and integrations from a centralized platform to improve visibility and operations at scale.",
            image: data.benefit1,
          },
          {
            tabTitle: "Seamless Deployment",
            title: "Register plug-and-play devices in as little as 5 minutes.",
            description:
              "Complex NVR/DVRs are a thing of the past. Rhombus’ modern architecture ensures fast and easy deployment from anywhere in the world. With simplified hardware infrastructure, installation is seamless with a single cable connection via PoE, eliminating the need for manual updates. ",
            image: data.benefit2,
          },
          {
            tabTitle: "Proactive Alerts",
            title:
              "Stay in the know with real-time alerts for faces, vehicles, and more.",
            description:
              "Save time, accelerate investigations, and receive real-time intelligent alerts with Rhombus AI Analytics. Through machine-learning, the system automatically learns what ‘normal’ looks like in your spaces and becomes smarter and more effective.",
            image: data.benefit4,
          },
          {
            tabTitle: "Easy Investigations",
            title:
              "Save time with multi-camera investigations and smart search.",
            description:
              "No more manual scrubbing. Camera and sensor data are layered together for fast investigations. Quickly search past footage, search for events, and securely share clips via email, text, or URL all from the Rhombus timeline.",
            image: data.benefit3,
          },
        ],
      },
      features: [
        {
          icon: data.icon1,
          iconAlt: "handshake",
          title: "Trusted Partner",
          p: "Our mission is to build a true partnership and provide simple, smart, and powerful solutions that fit your unique needs.",
        },
        {
          icon: data.icon2,
          iconAlt: "check mark",
          title: "Industry Veterans",
          p: "Since pioneering cloud security cameras, we’ve continued to create new, innovative ways to serve our customers. ",
        },
        {
          icon: data.icon3,
          iconAlt: "security",
          title: "Secure by Default",
          p: "Rhombus’ track record speaks for itself with zero-trust security framework and zero breaches.",
        },
        {
          icon: data.icon4,
          iconAlt: "support",
          title: "World-Class Support",
          p: "We guarantee you’ll receive a phenomenal experience from a team dedicated to your success.",
        },
      ],
    };

    return (
      <>
        <Helmet>
          <title>
            Rhombus Webinar - Next-Generation Enterprise Security Camera System
          </title>
          <meta
            name="description"
            content="Secure, cloud-managed video surveillance for enterprise, business, or commercial property. No DVR/NVRs required. Easy to manage. Infinitely scalable."
          />
        </Helmet>
        <WebinarHeader data={pageData.header} />
        <AboutSection data={pageData.aboutSection} />
        <InfoSlider
          data={pageData.benefits.list}
          title="The Benefits of Rhombus"
          color="var(--nuetral-100)"
        />
        <Features
          data={pageData.features}
          title="Rhombus Platform Features"
          color="#f4f4f4"
        />
        <IndustryLeadersCards />
      </>
    );
  };

  const query = graphql`
    query {
      headerImage: file(
        relativePath: {
          eq: "components/webinar/updated/img/webinar-header-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(
            layout: FULL_WIDTH
            quality: 100
            placeholder: BLURRED
          )
        }
      }
      benefit1: file(
        relativePath: { eq: "components/demo/img/infinite-scalability-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      benefit2: file(
        relativePath: { eq: "components/demo/img/seamless-deployment-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      benefit3: file(
        relativePath: { eq: "components/demo/img/proactive-alerts-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      benefit4: file(
        relativePath: { eq: "components/demo/img/easy-investigations-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      icon1: file(
        relativePath: { eq: "components/industries/img/icons/trust.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      icon2: file(
        relativePath: { eq: "components/industries/img/icons/system.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      icon3: file(
        relativePath: { eq: "components/demo/img/secure-by-default.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      icon4: file(relativePath: { eq: "components/demo/img/headset.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      competitorLogo: file(
        relativePath: { eq: "components/vs/updated/img/Verkada.png" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED)
        }
      }

      jeremy: file(
        relativePath: { eq: "components/webinar/img/jeremy-blue-bg-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100)
        }
      }
      trevor: file(
        relativePath: { eq: "components/webinar/img/trevor-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100)
        }
      }
    }
  `;

  return <StaticQuery query={query} render={renderContent} />;
}
